import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { authService } from '../services/authService';

const GoogleCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authenticateWithGoogle, authenticateWithToken } = useAuth();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        setLoading(true);
        
        
        // Check if there's an error from Google OAuth
        const errorParam = searchParams.get('error');
        if (errorParam) {
          throw new Error(`Google OAuth error: ${errorParam}`);
        }

        // Get the token and provider from URL parameters
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const code = searchParams.get('code');
        
        // Handle token-based authentication (backend already processed OAuth)
        if (token && provider === 'google') {
          
          try {
            // Use the new authenticateWithToken method
            await authenticateWithToken(token);
            
            showSnackbar('Successfully signed in with Google!', 'success');
            navigate('/', { replace: true });
            return;
          } catch (tokenError) {
            console.error('Token authentication failed:', tokenError);
            throw new Error(`Token authentication failed: ${tokenError instanceof Error ? tokenError.message : 'Unknown error'}`);
          }
        }
        
        // Handle code-based OAuth flow (fallback)
        if (code) {
          await authenticateWithGoogle();
          showSnackbar('Successfully signed in with Google!', 'success');
          navigate('/', { replace: true });
          return;
        }
        
        // If no token or code, check if we're already authenticated
        try {
          const userInfo = await authService.getCurrentUser();
          if (userInfo) {
            showSnackbar('Successfully signed in with Google!', 'success');
            navigate('/', { replace: true });
            return;
          }
        } catch (userError) {
          // User not authenticated, proceed with error handling
        }
        
        throw new Error('No authentication token or code received from Google');
      } catch (err) {
        console.error('Google OAuth callback error:', err);
        setError(err instanceof Error ? err.message : 'Failed to authenticate with Google');
        showSnackbar('Failed to sign in with Google', 'error');
        
        // Redirect to home page after a delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate, authenticateWithGoogle, authenticateWithToken, showSnackbar]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Completing Google sign-in...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Failed
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Current URL: {window.location.href}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Redirecting to home page...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return null;
};

export default GoogleCallback;
